import * as amplitude from "@amplitude/analytics-browser";
import { useHandler } from "@redotech/react-util/hook";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import {
  createContext,
  JSXElementConstructor,
  memo,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RedoBadgeColor } from "../../arbiter-components/badge/redo-badge";

export interface CommandPaletteFilter {
  id: string;
  label: string;
  enabled: boolean;
  visible: boolean;
}

export interface CommandPaletteItem {
  id: string;
  title: string;
  subtitle?: string;
  category: string;
  group: string;
  subgroup?: string;
  action: ({ newTab }: { newTab?: boolean }) => void | Promise<void>;
  icon?: ReactNode;
  tags?: { color: RedoBadgeColor; text: string }[];
  keywords?: string[];
  settingsResult?: {
    pageId: string;
    pageTitle: string;
    PageIcon?: JSXElementConstructor<any>;
    label: string;
    kind: "page" | "setting" | "group";
  };
}

export interface SearchParams {
  query: string;
  filters: string[];
  signal?: AbortSignal;
}

export type SearchCallback = (
  params: SearchParams,
) => Promise<CommandPaletteItem[]>;
export type ActionCallback = (search: string) => void;

export interface CommandPaletteState {
  searchValue: string;
  isOpen: boolean;
  selectedIndex: number;
  maxIndex: number;
  searchResults: CommandPaletteItem[];
  isSearching: boolean;
  filters: CommandPaletteFilter[];
  onAskAI?: (searchValue: string) => void;
}

export interface CommandPaletteActions {
  setSearchValue: (value: string) => void;
  setIsOpen: (open: boolean) => void;
  setSelectedIndex: (index: number) => void;
  setMaxIndex: (maxIndex: number) => void;
  setIsSearching: (searching: boolean) => void;
  openPalette: () => void;
  closePalette: () => void;
  togglePalette: () => void;
  clearSearch: () => void;
  navigateDown: () => void;
  navigateUp: () => void;
  open: () => void;
  openInNewTab: () => void;
  performSearch: (query: string) => Promise<void>;
  toggleFilter: (filterId: string) => void;
  setFilters: (filters: CommandPaletteFilter[]) => void;
  focusSearchInput: () => void;
}

export interface CommandPaletteContextValue {
  state: CommandPaletteState;
  actions: CommandPaletteActions;
  onSearch: SearchCallback;
  performAction?: (
    item: CommandPaletteItem,
    newTab?: { newTab?: boolean },
  ) => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(
  null,
);

/**
 * Lightweight context that exposes only whether the command palette is open.
 * Used by navigation components to conditionally render hidden items for
 * tree registration (global search) only when the palette is open.
 */
export const CommandPaletteOpenContext = createContext<boolean>(false);

export interface CommandPaletteProviderProps {
  children: ReactNode;
  defaultOpen?: boolean;
  onSearch: SearchCallback;
  onAction?: ActionCallback;
  onAskAI?: (searchValue: string) => void;
  initialResults?: string[];
  filters?: CommandPaletteFilter[];
}

export const CommandPaletteProvider = memo(function CommandPaletteProvider({
  children,
  defaultOpen = false,
  onSearch,
  onAction,
  onAskAI,
  initialResults = [],
  filters = [],
}: CommandPaletteProviderProps) {
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [maxIndex, setMaxIndex] = useState(initialResults.length);

  const [currentFilters, setCurrentFilters] =
    useState<CommandPaletteFilter[]>(filters);
  const exposedFilters = useMemo(() => {
    const incomingLabels = new Map(
      filters.map((filter) => [filter.id, filter.label]),
    );
    return currentFilters.map((filter) => {
      const label = incomingLabels.get(filter.id) ?? filter.label;
      return label === filter.label ? filter : { ...filter, label };
    });
  }, [currentFilters, filters]);

  // Track the current AbortController to cancel in-flight requests when user keeps typing
  const abortControllerRef = useRef<AbortController | null>(null);

  const performSearchWithFilters = useHandler(
    async (query: string, filters: string[]): Promise<void> => {
      // Abort any in-flight request before starting a new one
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (!onSearch || !query.trim()) {
        setSearchResults(recentResults);
        setMaxIndex(initialResults.length);
        return;
      }

      // Create a new AbortController for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsSearching(true);
      try {
        const results = await onSearch({
          query,
          filters,
          signal: controller.signal,
        });
        // Only update results if this request wasn't aborted
        if (!controller.signal.aborted) {
          setSearchResults(results);
          setMaxIndex(results.length);
          setSelectedIndex(0);
        }
      } catch (error) {
        // Ignore abort errors, they're expected when user keeps typing
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        console.error("Search failed:", error);
        if (!controller.signal.aborted) {
          setSearchResults([]);
          setMaxIndex(0);
        }
      } finally {
        // Only clear searching state if this is still the current request
        if (abortControllerRef.current === controller) {
          setIsSearching(false);
        }
      }
    },
  );

  const performSearch = useHandler(async (query: string): Promise<void> => {
    const enabledFilters = currentFilters
      .filter((filter) => filter.enabled)
      .map((filter) => filter.id);

    await performSearchWithFilters(query, enabledFilters);
  });

  const recentResults: CommandPaletteItem[] = useMemo(() => {
    return initialResults.map<CommandPaletteItem>((title, index) => {
      return {
        id: `recent-${index}`,
        title,
        category: "Recent",
        group: "Recent",
        icon: <ClockIcon />,
        action: () => {
          setSearchValue(title);
          void performSearch(title);
        },
      };
    });
  }, [initialResults, setSearchValue, performSearch]);

  const [searchResults, setSearchResults] =
    useState<CommandPaletteItem[]>(recentResults);
  const [isSearching, setIsSearching] = useState(false);

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const closePalette = useHandler(() => {
    setIsOpen(false);
    setSearchValue("");
    setSelectedIndex(0);
    setSearchResults(recentResults);
    setIsSearching(false);
  });

  const performAction = useHandler(
    (item: CommandPaletteItem, newTab: { newTab?: boolean } = {}) => {
      amplitude.track("nav/search-submitted", {
        query: searchValue,
        category: item.category,
      });
      void item.action?.(newTab);
      onAction?.(searchValue);
      if (item.group !== "Recent" && !newTab.newTab) {
        closePalette();
      }
    },
  );

  const toggleFilter = useCallback(
    (filterId: string) => {
      setCurrentFilters((prevFilters) => {
        const newFilters = prevFilters.map((filter) =>
          filter.id === filterId
            ? { ...filter, enabled: !filter.enabled }
            : filter,
        );

        const hasEnabledFilters = newFilters.some((filter) => filter.enabled);

        // Re-run search with current query and new filter state
        if (searchValue.trim()) {
          const enabledFilters = newFilters
            .filter((filter) => filter.enabled)
            .map((filter) => filter.id);

          void performSearchWithFilters(searchValue, enabledFilters);
        } else if (hasEnabledFilters) {
          // Clear recent results when a filter is enabled but no search query
          setSearchResults([]);
          setMaxIndex(0);
          setSelectedIndex(0);
        } else {
          // Restore recent results when all filters are disabled
          setSearchResults(recentResults);
          setMaxIndex(recentResults.length);
          setSelectedIndex(0);
        }

        return newFilters;
      });
    },
    [searchValue, performSearchWithFilters, recentResults],
  );

  const actions: CommandPaletteActions = useMemo(
    () => ({
      setSearchValue,
      setIsOpen,
      setSelectedIndex,
      setMaxIndex,
      setIsSearching,
      openPalette: () => {
        amplitude.track("nav/search-opened");
        setIsOpen(true);
      },
      closePalette,
      togglePalette: () => {
        if (isOpen) {
          setIsOpen(false);
          setSearchValue("");
          setSelectedIndex(0);
          setSearchResults(recentResults);
          setIsSearching(false);
        } else {
          amplitude.track("nav/search-opened");
          setIsOpen(true);
        }
      },
      clearSearch: () => {
        setSearchValue("");
        setSelectedIndex(0);
        setSearchResults(recentResults);
      },
      navigateDown: () => {
        const hasAskAi = !!onAskAI && searchValue.trim().length > 0;
        const minIndex = hasAskAi ? -1 : 0;
        setSelectedIndex((prev) => (prev < maxIndex - 1 ? prev + 1 : minIndex));
      },
      navigateUp: () => {
        const hasAskAi = !!onAskAI && searchValue.trim().length > 0;
        const minIndex = hasAskAi ? -1 : 0;
        setSelectedIndex((prev) => (prev > minIndex ? prev - 1 : maxIndex - 1));
      },
      open: () => {
        if (selectedIndex === -1 && onAskAI) {
          onAskAI(searchValue);
          closePalette();
          return;
        }
        const selectedItem = searchResults[selectedIndex];
        if (selectedItem) {
          performAction(selectedItem);
        }
      },
      openInNewTab: () => {
        const selectedItem = searchResults[selectedIndex];
        if (selectedItem) {
          performAction(selectedItem, { newTab: true });
        }
      },
      performSearch,
      toggleFilter,
      setFilters: setCurrentFilters,
      focusSearchInput: () => {
        searchInputRef.current?.focus();
      },
    }),
    [
      isOpen,
      maxIndex,
      searchResults,
      searchValue,
      selectedIndex,
      onAskAI,
      performSearch,
      toggleFilter,
      closePalette,
      performAction,
      recentResults,
    ],
  );

  const state: CommandPaletteState = {
    searchValue,
    isOpen,
    selectedIndex,
    maxIndex,
    searchResults,
    isSearching,
    filters: exposedFilters,
    onAskAI,
  };

  const contextValue: CommandPaletteContextValue = {
    state,
    actions,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    onSearch: onSearch!,
    performAction,
    searchInputRef,
  };

  // Sync searchResults with recentResults when there's no active search query
  // and no filters are enabled (i.e., showing the default/history view)
  useEffect(() => {
    const hasEnabledFilters = currentFilters.some((filter) => filter.enabled);
    if (!searchValue.trim() && !hasEnabledFilters && !isSearching) {
      setSearchResults(recentResults);
      setMaxIndex(recentResults.length);
    }
  }, [recentResults, searchValue, currentFilters, isSearching]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        event.stopPropagation();
        actions.togglePalette();
      } else if (isOpen) {
        switch (event.key) {
          case "Escape": {
            event.preventDefault();
            event.stopPropagation();
            actions.closePalette();

            break;
          }
          case "ArrowDown": {
            event.preventDefault();
            event.stopPropagation();
            actions.navigateDown();

            break;
          }
          case "ArrowUp": {
            event.preventDefault();
            event.stopPropagation();
            actions.navigateUp();

            break;
          }
          default:
            if (event.key === "Enter" && !event.metaKey && !event.ctrlKey) {
              event.preventDefault();
              event.stopPropagation();
              actions.open();
            } else if (
              event.key === "Enter" &&
              (event.metaKey || event.ctrlKey)
            ) {
              event.preventDefault();
              event.stopPropagation();
              actions.openInNewTab();
            }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [actions, isOpen, searchValue, searchResults]);

  return (
    <CommandPaletteContext.Provider value={contextValue}>
      <CommandPaletteOpenContext.Provider value={isOpen}>
        {children}
      </CommandPaletteOpenContext.Provider>
    </CommandPaletteContext.Provider>
  );
});

export function useCommandPalette(): CommandPaletteContextValue {
  const context = useContext(CommandPaletteContext);
  if (!context) {
    throw new Error(
      "useCommandPalette must be used within a CommandPaletteProvider",
    );
  }
  return context;
}
